import React from 'react';
import styled from 'styled-components';
import { FlexSpace } from './shared/FlexSpace';
import { theme } from '../style/theme';

const Box = styled(FlexSpace)<{ clickable?: boolean }>`
  flex-direction: column;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-height: 50px;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.primary.xlight};
  border: 1px solid ${theme.colors.primary.light};
  transition: all ${theme.transitions.fast};
  position: relative;
  overflow: hidden;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  
  /* Prevent mobile tap highlights and flashes */
  -webkit-tap-highlight-color: transparent;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
  
  &:hover {
    background: ${theme.colors.primary.light};
    border-color: ${props => props.clickable ? theme.colors.primary.main : theme.colors.neutral.gray200};
    transform: ${props => props.clickable ? 'translateY(-1px)' : 'none'};
    box-shadow: ${props => props.clickable ? theme.shadows.sm : 'none'};
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: ${theme.colors.primary.gradient};
    transform: scaleX(0);
    transition: transform ${theme.transitions.normal};
  }
  
  &:hover::before {
    transform: ${props => props.clickable ? 'scaleX(1)' : 'scaleX(0)'};
  }
  
  ${props => props.clickable && `
    &:active {
      transform: scale(0.98);
      box-shadow: ${theme.shadows.sm};
    }
    
    /* Mobile tap feedback - smoother and faster */
    @media (hover: none) and (pointer: coarse) {
      &:active {
        transform: scale(0.96);
        transition: transform 0.05s ease-out;
      }
    }
    
    /* Prevent focus outline flash on mobile */
    &:focus {
      outline: none;
    }
    
    &:focus-visible {
      outline: 2px solid ${theme.colors.primary.main};
      outline-offset: 2px;
    }
  `}
`;



const Title = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  align-items: center;
  color: ${theme.colors.neutral.gray600};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
  line-height: ${theme.typography.lineHeight.tight};
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const Value = styled.div`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.neutral.gray900};
  font-weight: ${theme.typography.fontWeight.bold};
  font-feature-settings: 'tnum';
  text-align: center;
`;

const ValueWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  min-height: 24px;
`;

interface IStatsBox {
  title: string;
  value: string | number;
  unit?: string;
  onClick?: () => void;
  clickable?: boolean;
}

const StatsBox = ({ title, value, unit, onClick, clickable = false }: IStatsBox) => {
  const displayValue = value !== null && value !== undefined ? value : '--';
  
  const handleClick = () => {
    if (clickable && onClick) {
      onClick();
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (clickable && onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };
  
  return (
    <Box 
      clickable={clickable}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={clickable ? 0 : undefined}
      role={clickable ? 'button' : undefined}
      aria-label={clickable ? `Learn more about ${title}` : undefined}
    >
      <Title>{title}</Title>
      <ValueWrapper>
        <Value>
          {displayValue}
          {unit && displayValue !== '--' && displayValue !== 'N/A' && (
            <span style={{ 
              fontSize: '0.8em', 
              color: theme.colors.neutral.gray500,
              marginLeft: '2px'
            }}>
              {unit}
            </span>
          )}
        </Value>
      </ValueWrapper>
    </Box>
  );
};

export default StatsBox;
